/**
 * Color-indexed product image helpers (noir-1, clip-noir-1, bleu-2, …).
 */
(() => {
  if (window.VidtoutColorMedia) return;

  const COLOR_NAME_MAP = {
    black: 'noir',
    noire: 'noir',
    blue: 'bleu',
    red: 'rouge',
    green: 'vert',
    verte: 'vert',
    white: 'blanc',
    blanche: 'blanc',
    gray: 'gris',
    grey: 'gris',
    grise: 'gris',
    yellow: 'jaune',
    pink: 'rose',
    purple: 'violet',
    mauve: 'violet',
    brown: 'marron',
    brun: 'marron',
    brune: 'marron',
    natural: 'beige',
    ecru: 'beige',
    burgundy: 'bordeaux',
    wine: 'bordeaux',
    teal: 'turquoise',
    cyan: 'turquoise',
    silver: 'argent',
    chrome: 'argent',
    gold: 'or',
    dore: 'or',
  };

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function colorKeyFromName(name) {
    const key = handleize(name);
    return COLOR_NAME_MAP[key] || key;
  }

  function parseCatalog(raw) {
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      if (!data || !Array.isArray(data.images)) return null;
      return data;
    } catch {
      return null;
    }
  }

  function getVisibleImages(catalog, colorKey) {
    if (!catalog?.images?.length) return [];
    if (!colorKey) return catalog.images;
    return catalog.images.filter((image) => !image.isColorIndexed || image.colorPrefix === colorKey);
  }

  function sortColorImages(images, colorKey) {
    return images
      .filter((image) => image.colorPrefix === colorKey)
      .sort((a, b) => {
        const aIndex = a.sortIndex || 0;
        const bIndex = b.sortIndex || 0;
        if (aIndex && bIndex) return aIndex - bIndex;
        if (aIndex) return -1;
        if (bIndex) return 1;
        return a.slug.localeCompare(b.slug);
      });
  }

  function getCardImages(catalog, colorKey) {
    const colorImages = sortColorImages(catalog?.images || [], colorKey);
    const primary = colorImages.find((img) => img.sortIndex === 1) || colorImages[0] || null;
    const hover = colorImages.find((img) => img.sortIndex === 2) || colorImages[1] || null;
    return { primary, hover };
  }

  function colorKeyFromVariant(catalog, variant) {
    if (!catalog || !variant) return '';
    const position = catalog.colorOptionPosition;
    if (!position) return '';
    const optionValue =
      variant[`option${position}`] ??
      (Array.isArray(variant.options) ? variant.options[position - 1] : '');
    return optionValue ? colorKeyFromName(optionValue) : '';
  }

  function colorKeyFromPicker(root, catalog) {
    if (!root || !catalog?.colorOptionPosition) return '';
    const fieldsetIndex = catalog.colorOptionPosition - 1;
    const picker = root.querySelector('variant-picker');
    if (!picker) return '';

    const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${fieldsetIndex}"]`);
    const checked = fieldset?.querySelector('input[type="radio"]:checked');
    if (checked) {
      if (checked.dataset.colorKey) return checked.dataset.colorKey;
      const label = checked.getAttribute('aria-label') || checked.value || '';
      return label ? colorKeyFromName(label.split(' - ')[0]) : '';
    }

    const selects = picker.querySelectorAll('select.variant-option__select');
    for (const select of selects) {
      const selected = select.options[select.selectedIndex];
      if (!selected?.dataset.inputId?.startsWith(`${catalog.colorOptionPosition}-`)) continue;
      if (selected.dataset.colorKey) return selected.dataset.colorKey;
      return selected.value ? colorKeyFromName(selected.value.split(' - ')[0]) : '';
    }

    return '';
  }

  function resolveColorKey(root, catalog, variant) {
    return (
      colorKeyFromVariant(catalog, variant) ||
      colorKeyFromPicker(root, catalog) ||
      ''
    );
  }

  function sortGalleryThumbs(thumbs) {
    return thumbs.slice().sort((a, b) => {
      const aIndexed = a.dataset.colorIndexed === 'true';
      const bIndexed = b.dataset.colorIndexed === 'true';

      // Color-specific images first (bleu-1, bleu-2…), then generic (clipsac, etc.)
      if (aIndexed && !bIndexed) return -1;
      if (!aIndexed && bIndexed) return 1;

      if (aIndexed && bIndexed) {
        const aSort = Number(a.dataset.colorSortIndex) || 0;
        const bSort = Number(b.dataset.colorSortIndex) || 0;
        if (aSort !== bSort) return aSort - bSort;
      }

      return Number(a.dataset.thumbIndex) - Number(b.dataset.thumbIndex);
    });
  }

  window.VidtoutColorMedia = {
    colorKeyFromName,
    parseCatalog,
    getVisibleImages,
    getCardImages,
    colorKeyFromVariant,
    colorKeyFromPicker,
    resolveColorKey,
    sortGalleryThumbs,
  };
})();
